<script setup>
/**
 * 牌九「开奖记录」弹窗，逐条对齐旧版 web/19.0d8589924b85d555a0a0.js 里的
 * app-lottery 组件（类 b、模板工厂 yn + 子模板 Cn/On/_n/fn/Mn、样式 hn）。
 *
 * 旧版打开点：右侧菜单「开奖」→ historyModal()
 *   modalCtrl.create({ component: b, componentProps: { game } })
 *
 * 旧版类 b：
 *   构造：game = navParams.get('game')，lists = []，page = new Page()
 *   ngOnInit → getHistory()
 *   getHistory(l): getLotteryList({game: game.game_id, page: page.page_index}) → POST /game/history
 *     成功：page = t.page
 *           lists = page_index === 1 ? t.data : lists.concat(t.data)   ← 注意是 t.data 本身，不是 t.data.lists
 *           lists = Array.from(new Set(lists))
 *     结尾：l && l.target.complete()
 *   doRefresh(l): page_index = 1; getHistory(l)
 *   doInfinite(l): page_index += 1; 超过 page_count 就 toast「已经最后一页了」并 complete()
 */
import { computed, onMounted, ref } from 'vue'
import { request } from '../services/api'
import PullRefresher from './PullRefresher.vue'

const props = defineProps({
  // 旧版 navParams.get('game')，只用到 game_id
  game: { type: Object, required: true },
})
const emit = defineEmits(['close'])

const lists = ref([])
// 后端 Api::page() 结构
const page = ref({ item_total: 0, page_index: 1, page_size: 15, page_count: 1 })
const loading = ref(false)
const notice = ref('')

const gameId = computed(() => props.game?.game_id)
const isOzbPj = computed(() => String(props.game?.game_name || '').toLowerCase() === 'ozbpj')
// 旧版 ion-infinite-scroll 的 ngIf：page_count>1 && page_count!=page_index
const hasMore = computed(() => page.value.page_count > 1 && page.value.page_count != page.value.page_index)

/**
 * 旧版 {{ history_lottery_time | amDateFormat:'MM-DD HH:mm' }}
 * 后端可能给秒级时间戳，也可能给 'Y-m-d H:i:s'，这里两种都兼容。
 */
function fmtTime(value) {
  if (!value) return ''
  const raw = String(value)
  const date = new Date(/^\d+$/.test(raw) ? Number(raw) * 1000 : raw.replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return raw
  const pad = (number) => String(number).padStart(2, '0')
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** 旧版 getHistory(l=null) */
async function getHistory(event = null) {
  if (!gameId.value) { event?.complete(); return }
  loading.value = true
  try {
    const response = await request('/game/history', {
      method: 'POST',
      data: { game: gameId.value, page: page.value.page_index },
    })
    page.value = { ...page.value, ...(response.page || {}) }
    const incoming = Array.isArray(response.data) ? response.data : []
    lists.value = page.value.page_index === 1 ? incoming : [...lists.value, ...incoming]
  } catch (error) {
    notice.value = error.message || '加载失败'
  } finally {
    loading.value = false
    event?.complete()
  }
}

/** 旧版 doRefresh(l) */
function doRefresh(event) {
  page.value = { ...page.value, page_index: 1 }
  void getHistory(event)
}

/** 旧版 doInfinite(l) */
function doInfinite() {
  const next = page.value.page_index + 1
  if (next > page.value.page_count) { notice.value = '已经最后一页了'; return }
  page.value = { ...page.value, page_index: next }
  void getHistory()
}

// 旧版 ngOnInit
onMounted(() => { void getHistory() })
</script>

<template>
  <div class="history-modal">
    <!-- 旧版 ion-header > ion-toolbar：arrow-back + 标题「开奖记录」 -->
    <header class="modal-header">
      <button class="modal-back" type="button" aria-label="返回" @click="emit('close')">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </button>
      <h1 class="modal-title">开奖记录</h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <!-- 旧版 ion-refresher：pullingIcon="arrow-dropdown" pullingText="下拉刷新"
         refreshingSpinner="circles" refreshingText="下拉刷新..." -->
    <PullRefresher
      pulling-text="下拉刷新"
      refreshing-text="下拉刷新..."
      refreshing-spinner="circles"
      @refresh="doRefresh"
    >
      <!-- 旧版 Cn：ion-list + ngFor lists → On。注意这一段没有 ion-list-header -->
      <section v-if="lists.length" class="ion-list">
        <div v-for="(row, index) in lists" :key="row.game_no ?? index" class="list-item">
          <div class="list-item-inner">
            <!-- 旧版 On：div.lottery-lists -->
            <div class="lottery-lists">
              <div class="lottery-game-no">
                <div>{{ row.game_no }}</div>
                <div>{{ fmtTime(row.history_lottery_time) }}</div>
              </div>
              <!-- 旧版 _n：ngFor format_lottery_numbers.result -->
              <div
                v-for="(item, itemIndex) in (row.format_lottery_numbers?.result || [])"
                :key="itemIndex"
                class="lottery-content-item"
              >
                <div class="lottery-ball">
                  <!-- 旧版 fn：<img src="./assets/images/game/paijiu/ball/{{card}}.png">，直接拼不补零 -->
                  <img
                    v-if="!isOzbPj"
                    v-for="(card, cardIndex) in (item.item || [])"
                    :key="`image-${cardIndex}`"
                    :src="`/legacy/images/game/paijiu/ball/${card}.png`"
                    alt=""
                  />
                  <span v-else class="ozbpj-number">{{ item.sum }}</span>
                </div>
                <div class="lottery-sum"> {{ item.sum }}点 </div>
                <div v-if="!isOzbPj" class="lottery-rank"> {{ item.rank }} </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 旧版 Mn：ion-infinite-scroll-content loadingSpinner="bubbles" loadingText="正在加载"，
           滚动到底自动触发；这里没有 Ionic，退化成按钮 -->
      <button v-if="hasMore" class="load-more" type="button" :disabled="loading" @click="doInfinite">
        正在加载
      </button>
      <p v-if="notice" class="modal-notice">{{ notice }}</p>
    </PullRefresher>
  </div>
</template>

<style scoped>
/* 弹窗外壳与顶栏跟其它牌九弹窗一致，顶栏色取旧版主题变量 #f95454 */
.history-modal{position:fixed;z-index:30;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;line-height:1;color:inherit;cursor:pointer;padding:0}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}

/* --- Ionic 4 md 的真实默认值，取自 web/135.773ae5d979ae5c36fbef.js --- */
/* ion-list（.list-md） */
.ion-list{display:block;margin:0;padding:8px 0;background:#fff;list-style-type:none}
/* ion-item（md）：外层 .item-native + 内层 .item-inner */
.list-item{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:48px;margin:0;padding:0 0 0 16px;background:#fff;color:#000;font-size:.8rem;overflow:hidden;position:relative;box-sizing:border-box}
.list-item-inner{display:flex;flex:1;flex-direction:inherit;align-items:inherit;align-self:stretch;min-height:inherit;padding:0 16px 0 0;border-width:0 0 1px 0;border-style:solid;border-color:rgba(0,0,0,.13);overflow:hidden;box-sizing:border-box}

/* --- 以下逐条照抄旧版样式表 hn（chunk 19），:host → .history-modal --- */
.history-modal{background:#fff}
.lottery-lists{font-size:.8rem;display:flex;align-items:center;padding-top:5px;padding-bottom:5px;width:100%}
.lottery-lists .lottery-game-no{width:16.66%}
.lottery-lists .lottery-content-item{width:16.66%;text-align:center}
.lottery-lists .lottery-content-item .lottery-ball img{width:45%}
.lottery-lists .lottery-content-item .lottery-ball .ozbpj-number{display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;min-width:25px;height:25px;padding:0 6px;border:1px solid #8ed0ff;border-radius:50%;background:#1479d1;color:#fff;font-weight:700;font-size:14px;line-height:1}
.lottery-lists .lottery-content-item .lottery-sum{color:red}
.lottery-lists .lottery-content-item .lottery-rank{color:#5f9ea0}

.load-more{display:block;width:calc(100% - 32px);margin:10px 16px;padding:8px 0;border:1px solid #ddd;border-radius:4px;background:#fafafa;font-size:.8rem}
.modal-notice{margin:10px 16px;color:#999;font-size:.8rem;text-align:center}
</style>
